import React, { useState } from "react";
import {
  Modal,
  Button,
  Upload,
  UploadFile,
  Alert,
  Table,
  Typography,
  Space,
  message,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  MeetingImportCreated,
  MeetingImportFailed,
  MeetingImportResult,
  useMeetingImport,
} from "../../../../hooks/api/vaads";
import { getMessageInError } from "../../../../hooks/fetch";
import { dateTimeFormat } from "../../../../contstant";
import { useMeetingsExportExample } from "./useMeetExemple";

interface ImportMeetingModalProps {
  open: boolean;
  onClose: () => void;
}

function getFailedMessage(item: MeetingImportFailed): string {
  if (item.message) return item.message;
  if (item.reason) return item.reason;
  if (Array.isArray(item.errors)) return item.errors.join(", ");
  if (typeof item.errors === "string") return item.errors;
  return "-";
}

function ImportResults({ result }: { result: MeetingImportResult }) {
  const failedCount =
    (result.vaadNotFound?.length || 0) + (result.validationFailed?.length || 0);

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert
        type={failedCount > 0 ? "warning" : "success"}
        showIcon
        message={`Imported ${result.created?.length || 0} of ${result.totalRows} rows`}
        description={
          failedCount > 0
            ? `${failedCount} row(s) failed`
            : "All rows imported successfully"
        }
      />

      {!!result.created?.length && (
        <>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Created ({result.created.length})
          </Typography.Title>
          <Table<MeetingImportCreated>
            size="small"
            rowKey={(r) => `${r.row}-${r.meetingId}`}
            dataSource={result.created}
            pagination={false}
            scroll={{ y: 220 }}
            columns={[
              { title: "Row", dataIndex: "row", width: 60 },
              { title: "Vaad ID", dataIndex: "vaadId", width: 90 },
              { title: "Name", dataIndex: "name" },
              {
                title: "Start",
                dataIndex: "startTime",
                width: 150,
                render: (v: string) =>
                  dayjs.utc(v).isValid()
                    ? dayjs.utc(v).format(dateTimeFormat)
                    : v,
              },
              {
                title: "End",
                dataIndex: "endTime",
                width: 150,
                render: (v: string) =>
                  dayjs.utc(v).isValid()
                    ? dayjs.utc(v).format(dateTimeFormat)
                    : v,
              },
            ]}
          />
        </>
      )}

      {!!result.vaadNotFound?.length && (
        <>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Vaad not found ({result.vaadNotFound.length})
          </Typography.Title>
          <Table<MeetingImportFailed>
            size="small"
            rowKey={(r, i) => `vaad-not-found-${r.row}-${i}`}
            dataSource={result.vaadNotFound}
            pagination={false}
            scroll={{ y: 160 }}
            columns={[
              { title: "Row", dataIndex: "row", width: 60 },
              { title: "Vaad ID", dataIndex: "vaadId", width: 90 },
              {
                title: "Reason",
                render: (_, record) => getFailedMessage(record),
              },
            ]}
          />
        </>
      )}

      {!!result.validationFailed?.length && (
        <>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Validation failed ({result.validationFailed.length})
          </Typography.Title>
          <Table<MeetingImportFailed>
            size="small"
            rowKey={(r, i) => `validation-failed-${r.row}-${i}`}
            dataSource={result.validationFailed}
            pagination={false}
            scroll={{ y: 160 }}
            columns={[
              { title: "Row", dataIndex: "row", width: 60 },
              { title: "Vaad ID", dataIndex: "vaadId", width: 90 },
              { title: "Name", dataIndex: "name" },
              {
                title: "Reason",
                render: (_, record) => getFailedMessage(record),
              },
            ]}
          />
        </>
      )}
    </Space>
  );
}

export default function ImportMeetingModal({
  open,
  onClose,
}: ImportMeetingModalProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [result, setResult] = useState<MeetingImportResult | null>(null);
  const meetingsImport = useMeetingImport();
  const exportExample = useMeetingsExportExample();

  const handleClose = () => {
    setFileList([]);
    setResult(null);
    meetingsImport.clearError();
    onClose();
  };

  const handleSubmit = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      message.warning("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await meetingsImport.fetch(formData);
      if (response instanceof Error || (response as any)?.isAxiosError) {
        throw response;
      }
      if (response) {
        setResult(response);
        message.success("Import completed");
      }
    } catch (error) {
      message.error(getMessageInError(error) || "Import failed");
    }
  };

  const handleDownloadExample = async () => {
    try {
      await exportExample.download();
    } catch (error) {
      message.error(getMessageInError(error) || "Download failed");
    }
  };

  return (
    <Modal
      title="Import Meeting"
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      width={result ? 820 : 480}
      footer={
        result
          ? [
            <Button key="close" onClick={handleClose}>
              Close
            </Button>,
          ]
          : [
            <Button key="cancel" onClick={handleClose}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              icon={<UploadOutlined />}
              loading={meetingsImport.loading}
              disabled={!fileList.length || meetingsImport.loading}
              onClick={handleSubmit}
            >
              Submit
            </Button>,
          ]
      }
    >
      {result ? (
        <ImportResults result={result} />
      ) : (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Upload.Dragger
            accept=".xlsx,.csv"
            maxCount={1}
            fileList={fileList}
            beforeUpload={(file) => {
              setFileList([
                {
                  uid: file.uid,
                  name: file.name,
                  status: "done",
                  originFileObj: file,
                },
              ]);
              return false;
            }}
            onRemove={() => {
              setFileList([]);
              return true;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area</p>
            <p className="ant-upload-hint">Supports .xlsx, .csv</p>
          </Upload.Dragger>

          {meetingsImport.error && (
            <Alert
              type="error"
              showIcon
              message={getMessageInError(meetingsImport.error)}
              closable
              onClose={meetingsImport.clearError}
            />
          )}

          <Button
            type="link"
            icon={<DownloadOutlined />}
            loading={exportExample.loading}
            onClick={handleDownloadExample}
            style={{ padding: 0 }}
          >
            Download example file
          </Button>
        </Space>
      )}
    </Modal>
  );
}
